import classNames from 'classnames';
import PageSection from '../PageSection/PageSection';
import React, {
  FunctionComponent,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import styles from './ActivityTotals.scss';
import { ActionLinks } from './ActionLinks/ActionLinks';
import { ExtraMeasurements } from './ExtraMeasurements/ExtraMeasurements';
import { FilterButtons } from './FilterButtons/FilterButtons';
import {
  getMeasurementSystemFromSettings,
  getDistanceUnitForMeasurementSystem,
} from '../../utils/measurementSystem';
import { ProgressBar } from './ProgressBar/ProgressBar';
import { useFlags } from '../../hooks/useFlags';
import { useTranslation } from 'react-i18next';
import {
  ActivityTotalsFragment,
  PageTotal,
  PageTotalsDisplayOptions,
  TimeUnit,
  DistanceUnit,
  MeasurementSystem,
} from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import {
  ActivityFilters,
  buildFilteredTotals,
  ActivityTotal,
  ActivityTotalMeasurementTypes,
  mapMeasurementTypeToActivityTotalMeasurementType,
} from './filters';
import { activityTotalsFragment } from '../../fragments/activityTotalsFragment';
import { FormattedMeasurement } from '../Formatters/FormattedMeasurement';

const FRAGMENT = buildQuery(activityTotalsFragment);

const DefaultPrimaryMeasurementType: ActivityTotalMeasurementTypes = 'distance';

export const ActivityTotals: FunctionComponent = () => {
  let [activityFilter, setActivityFilter] = useState(ActivityFilters.all.key);

  // NOTE: This enables switching the primary measurement type in the browser.
  // TODO: This should be removed once design of this component is complete.
  const [
    customPrimaryMeasurementType,
    setCustomPrimaryMeasurementType,
  ] = useState<ActivityTotalMeasurementTypes | undefined>(undefined);

  const { t } = useTranslation('common', { useSuspense: false });

  const { virtualFitnessEventsDebugFeatures } = useFlags();

  const result = usePageQuery<ActivityTotalsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (
    !UsePageQueryResult.hasData(result) ||
    isHiddenByDisplayOptions(result.data)
  ) {
    return null;
  }

  const primaryMeasurementTypeToUse =
    customPrimaryMeasurementType ??
    getPrimaryMeasurementType(result.data) ??
    DefaultPrimaryMeasurementType;

  const settings = result.data?.application?.settings;
  const measurementSystem = getMeasurementSystemFromSettings(settings);
  const timelineTotals: PageTotal[] = result.data?.totals?.timeline ?? [];

  if (settings && !settings.showTotaliser) {
    return null;
  }

  const { activityTypes, activityTotalsMap } = buildViewData(
    timelineTotals,
    measurementSystem,
    activityFilter,
    setActivityFilter,
  );

  const allTotal = activityTotalsMap[ActivityFilters.all.key];
  const allPrimaryMeasurement = allTotal?.[primaryMeasurementTypeToUse];

  if (!allPrimaryMeasurement?.value) {
    return null;
  }

  const total = activityTotalsMap[activityFilter];
  const primaryMeasurement = total[primaryMeasurementTypeToUse];

  return (
    <PageSection>
      <div className={styles.box}>
        <h1 className="jg-h4">{t('activity.totals.heading')}</h1>
        <FilterButtons
          activityTypes={activityTypes}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
        />
        <div className={classNames('jg-text--regular-light', styles.summary)}>
          <strong>
            <FormattedMeasurement value={primaryMeasurement} />
          </strong>
          {activityFilter === ActivityFilters.all.key &&
            ` ${t('activity.totals.completedOverall')}`}
        </div>
        {virtualFitnessEventsDebugFeatures && (
          <ProgressBar percent={55}></ProgressBar>
        )}
        {virtualFitnessEventsDebugFeatures && (
          <ExtraMeasurements
            total={total}
            primaryMeasurementType={primaryMeasurementTypeToUse}
          />
        )}
        <ActionLinks
          switchPrimaryMeasurementType={() =>
            setCustomPrimaryMeasurementType(
              getNextPrimaryMeasurementType(primaryMeasurementTypeToUse),
            )
          }
        />
      </div>
    </PageSection>
  );
};

const isHiddenByDisplayOptions = (data: ActivityTotalsFragment): boolean =>
  data.totals?.displayOptions === PageTotalsDisplayOptions.DoNotDisplay;

const buildViewData = (
  timelineTotals: PageTotal[],
  measurementSystem: MeasurementSystem,
  activityFilter: string,
  setActivityFilter: Dispatch<SetStateAction<string>>,
) => {
  if (timelineTotals.length === 0) {
    return {
      activityTypes: [ActivityFilters.all.key],
      activityTotalsMap: {
        [ActivityFilters.all.key]: buildDefaultZeroTotal(
          getDistanceUnitForMeasurementSystem(measurementSystem),
        ),
      },
    };
  } else {
    const {
      activityTotalsMap,
      activityTypes,
      isSingleActivityMode,
    } = buildFilteredTotals(measurementSystem, timelineTotals);

    if (
      isSingleActivityMode &&
      activityFilter === ActivityFilters.all.key &&
      activityTypes.length > 0
    ) {
      // The initial value of activityFilter is "all".
      // However, we remove "all" if there is only one activity type.
      // So, switch the current filter over to that one type.
      setActivityFilter(activityTypes[0]);
    }

    return {
      activityTypes,
      activityTotalsMap,
    };
  }
};

const buildDefaultZeroTotal = (distanceUnit: DistanceUnit): ActivityTotal => ({
  distance: {
    unit: distanceUnit,
    value: 0,
  },
  duration: {
    unit: TimeUnit.Seconds,
    value: 0,
  },
  elevation: {
    unit: distanceUnit,
    value: 0,
  },
});

const getPrimaryMeasurementType = (
  resultData: ActivityTotalsFragment | undefined,
): ActivityTotalMeasurementTypes | undefined =>
  resultData?.totals?.primaryMeasurementType
    ? mapMeasurementTypeToActivityTotalMeasurementType(
        resultData.totals.primaryMeasurementType,
      )
    : undefined;

const measurementTypes: ActivityTotalMeasurementTypes[] = [
  'distance',
  'duration',
  'elevation',
];

const getNextPrimaryMeasurementType = (
  primaryMeasurementType: ActivityTotalMeasurementTypes,
): ActivityTotalMeasurementTypes => {
  const currentIndex = measurementTypes.indexOf(primaryMeasurementType);
  return measurementTypes[(currentIndex + 1) % measurementTypes.length];
};
